import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ReportPreviewFrame } from '@/components/patientReports/ReportPreviewFrame';
import { useClinic } from '@/hooks/useClinicSettings';
import { usePatientReportSettings } from '@/hooks/usePatientReports';
import { loadReportAuthorName } from '@/lib/patientReports/docxFile';
import type { PatientReportLetterheadChoice } from '@/lib/patientReports/model';
import {
  reportDateRevisionKey,
  withReportDateAtGeneration,
  type PlaceholderFillContext,
} from '@/lib/patientReports/placeholders';
import { printPdfUrl, savePdfUrl } from '@/lib/patientReports/previewPdf';
import { materializeReportPdf } from '@/lib/patientReports/reportDocument';
import { Button } from '@/components/ui/Button';
import { TYPE } from '@/lib/typography';
import { cn } from '@/utils/cn';

/**
 * PDF preview of the filled Word template.
 * The cover sheet is in front. Letterhead is the chrome on the report pages.
 */
export function WordReportPreview({
  clinicId,
  wordPath,
  coverPath,
  coverMime,
  includeCover,
  letterhead,
  context,
  blocks,
  patientId,
  canRevealCpr,
  sampleCpr,
  fallback,
  sourceRevision = 0,
  headingAside,
}: {
  clinicId: string | null;
  wordPath: string;
  coverPath: string | null;
  coverMime: string | null;
  includeCover: boolean;
  letterhead: PatientReportLetterheadChoice;
  context: PlaceholderFillContext;
  blocks: readonly { heading: string; body: string }[];
  patientId: string | null;
  canRevealCpr: boolean;
  sampleCpr?: string | null;
  fallback: ReactNode;
  /** Bumps when the file at wordPath is replaced. The path itself stays the same. */
  sourceRevision?: number;
  /** Sits to the right of the title and hint so that text wraps sooner. */
  headingAside?: ReactNode;
}) {
  const { t } = useTranslation();
  const settings = usePatientReportSettings(clinicId);
  const clinic = useClinic(clinicId);
  const [pdf, setPdf] = useState<Uint8Array | null>(null);
  const [pending, setPending] = useState(true);
  const [failed, setFailed] = useState(false);
  const inputRef = useRef({ context, blocks });
  inputRef.current = { context, blocks };

  const revision = useMemo(
    () =>
      JSON.stringify({
        wordPath,
        coverPath,
        coverMime,
        includeCover,
        letterhead,
        patientId,
        canRevealCpr,
        sampleCpr: sampleCpr ?? null,
        title: context.reportTitle,
        reportDate: reportDateRevisionKey(context),
        coverTitle: context.coverTitle,
        coverBody: context.coverBody,
        cpr: sampleCpr != null ? null : context.cpr,
        cprAccess: context.cprAccess,
        patient: context.patient?.id ?? null,
        name: context.patient?.name ?? '',
        journal: context.journal,
        markings: context.markings,
        questionnaire: context.questionnaire,
        files: context.files,
        text: context.text,
        content: context.content,
        toc: context.toc,
        marks: (context.contentPieces ?? [])
          .map((piece) =>
            piece.type === 'heading'
              ? piece.style
              : piece.type === 'toc'
                ? `${piece.headingStyle ?? 'bold'}:${piece.entries.map((entry) => entry.style ?? 'bold').join(',')}`
                : '',
          )
          .join('|'),
        blocks,
        sourceRevision,
      }),
    [
      blocks,
      canRevealCpr,
      context,
      coverMime,
      coverPath,
      includeCover,
      letterhead,
      patientId,
      sampleCpr,
      sourceRevision,
      wordPath,
    ],
  );

  useEffect(() => {
    if (!clinicId) {
      setFailed(true);
      setPending(false);
      return;
    }
    if (!settings.data) return;
    const settingsRow = settings.data;
    const clinicRow = clinic.data;
    let cancelled = false;
    setPending(true);
    const timer = window.setTimeout(() => {
      const { context: placeholders, blocks: blockSnapshot } = inputRef.current;
      void (async () => {
        try {
          const authorName = await loadReportAuthorName();
          const bytes = await materializeReportPdf({
            settings: settingsRow,
            clinic: clinicRow,
            letterhead,
            wordPath,
            coverPath,
            coverMime,
            includeCover,
            placeholders: withReportDateAtGeneration({
              ...placeholders,
              authorName,
              clinicName: clinicRow?.name ?? '',
            }),
            title: placeholders.reportTitle,
            blocks: blockSnapshot,
            patientId,
            canRevealCpr,
            sampleCpr,
          });
          if (cancelled) return;
          setPdf(bytes);
          setFailed(false);
          setPending(false);
        } catch {
          if (cancelled) return;
          setPdf(null);
          setFailed(true);
          setPending(false);
        }
      })();
    }, 300);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [
    canRevealCpr,
    clinic.data,
    clinicId,
    coverMime,
    coverPath,
    includeCover,
    letterhead,
    patientId,
    revision,
    sampleCpr,
    settings.data,
    wordPath,
  ]);

  const url = useMemo(() => {
    if (!pdf) return null;
    const copy = new Uint8Array(pdf.byteLength);
    copy.set(pdf);
    return URL.createObjectURL(new Blob([copy], { type: 'application/pdf' }));
  }, [pdf]);

  useEffect(() => {
    return () => {
      if (url) URL.revokeObjectURL(url);
    };
  }, [url]);

  const lead = (
    <>
      <h3 className={cn(TYPE.section, 'text-ds-text')}>{t('patientReports.preview')}</h3>
      <p className={cn(TYPE.small, 'mt-s1 text-ds-muted')}>
        {t('patientReports.preview_word_hint')}
      </p>
    </>
  );

  return (
    <section aria-label={t('patientReports.preview')}>
      {headingAside ? (
        <div className="flex items-start gap-s3" data-testid="report-preview-lead">
          <div className="min-w-0 flex-1">{lead}</div>
          <div className="flex shrink-0 flex-wrap items-center justify-end gap-s2">
            {headingAside}
            <Button
              type="button"
              variant="secondary"
              disabled={!url}
              data-testid="report-print"
              onClick={() => {
                if (url) printPdfUrl(url);
              }}
            >
              {t('patientReports.print')}
            </Button>
            <Button
              type="button"
              variant="secondary"
              disabled={!url}
              data-testid="report-download-pdf"
              onClick={() => {
                if (!url) return;
                const name = context.reportTitle.trim() || t('patientReports.untitled');
                savePdfUrl(url, `${name}.pdf`);
              }}
            >
              {t('patientReports.download')}
            </Button>
          </div>
        </div>
      ) : (
        lead
      )}
      {pending ? (
        <p className={cn(TYPE.small, 'mt-s3 text-ds-muted')}>
          {t('patientReports.preview_word_loading')}
        </p>
      ) : null}
      {failed ? (
        <p className={cn(TYPE.small, 'mt-s3 text-ds-red-ink')}>
          {t('patientReports.preview_word_error')}
        </p>
      ) : null}
      {url ? (
        <ReportPreviewFrame
          className="mt-s4"
          title={t('patientReports.preview')}
          src={url}
          iframeTestId="report-pdf-preview"
        />
      ) : null}
      {failed ? <div className="mt-s4">{fallback}</div> : null}
    </section>
  );
}
